/**
 * Watch list + Web Push API client (D15).
 *
 * Auth (2026-07-22 SECURITY): every authenticated call sends
 * `Authorization: Bearer <token>` (a Clerk session token from
 * useAuth().getToken()), verified server-side by get_current_user. This
 * replaced the legacy `x-mio-user-id` header, which the API only checked
 * for presence - any value passed, so a stranger could create/scan
 * watches (each runs a PAID price search) and read/delete others'
 * watches. Watch is now a signed-in action (spec 4). The public-key
 * endpoint stays unauthenticated (it returns only the VAPID public key).
 */

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return process.env.API_INTERNAL_URL ?? process.env.API_URL ?? "http://localhost:8000";
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export interface WatchTargetSpec {
  retailer_slug: string;
  product_url: string;
  fetch_adapter?: string;
  scraping_tier?: number;
  country_code?: string;
}

export interface CreateWatchBody {
  label: string;
  targets: WatchTargetSpec[];
  user_postcode?: string;
  target_price_aud?: string;
  country_code?: string;
  // 2026-07-24: seed the baseline from the True Final Price the page already
  // shows so the watch is created instantly (no synchronous re-crawl on the
  // click). Omit to fall back to the server's initial scan.
  baseline_true_final_price_aud?: string;
  current_best_retailer_slug?: string;
}

export interface WatchView {
  id: string;
  label: string;
  country_code: string;
  user_postcode: string | null;
  target_price_aud: string | null;
  baseline_true_final_price_aud: string | null;
  current_true_final_price_aud: string | null;
  current_best_retailer_slug: string | null;
  status: string;
  last_scanned_at: string | null;
  last_alert_sent_at: string | null;
  monitor_until: string | null;
  is_auto_monitor: boolean;
  created_at: string;
  // Best in-stock retailer's product URL, for the "Buy at <shop>" button.
  // null when the current-best retailer isn't in the stored targets.
  current_best_product_url: string | null;
  // Raw decision from the last scan; the frontend reads it after polling to
  // pick the result banner (up vs no change is derived from the prices).
  last_scan_decision: string | null;
  // B-246 (2026-09-19): the /c/<slug> page this watch is about, or null.
  // Absent on an older api.
  comparison_slug?: string | null;
}

export interface WebPushPublicKeyResponse {
  public_key: string | null;
  enabled: boolean;
}

export class WatchesApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }

  // A shopper-facing message the API deliberately sent (e.g. the active-watch
  // cap), extracted from FastAPI's {"detail": {"message": "..."}} shape.
  // Null for every other failure so callers keep their generic copy.
  serverMessage(): string | null {
    const msg = this._innerDetail()?.message;
    return typeof msg === "string" && msg.trim() ? msg : null;
  }

  // Machine-readable error code from the same shape (e.g.
  // "watch_limit_reached"). Callers branch on THIS, never on message text.
  serverCode(): string | null {
    const code = this._innerDetail()?.error;
    return typeof code === "string" && code ? code : null;
  }

  isWatchLimit(): boolean {
    return this.serverCode() === "watch_limit_reached";
  }

  private _innerDetail(): { message?: unknown; error?: unknown } | null {
    const outer = this.detail as { detail?: unknown } | null;
    const inner = outer && typeof outer === "object" ? outer.detail : null;
    return inner && typeof inner === "object"
      ? (inner as { message?: unknown; error?: unknown })
      : null;
  }
}

async function _post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${apiBaseUrl()}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new WatchesApiError(res.status, detail, `POST ${path} returned ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function createWatch(
  body: CreateWatchBody,
  token: string,
): Promise<WatchView> {
  return _post<WatchView>("/api/watches", body, token);
}

export async function listWatches(token: string): Promise<WatchView[]> {
  const res = await fetch(`${apiBaseUrl()}/api/watches`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new WatchesApiError(res.status, detail, `GET /api/watches returned ${res.status}`);
  }
  return (await res.json()) as WatchView[];
}

/**
 * Re-price a watch's EXACT tracked retailers and return the full comparison.
 * Used by the "See all prices" button so the shopper sees the SAME retailer
 * set and cheapest-in-stock price the watch card shows, not a fresh narrow
 * re-search of the product name. Owner-scoped; the backend caches the result
 * so repeat views are free. Returns the SearchResult (same shape the /c pages
 * render); the caller casts it, mirroring web/app/c/[slug]/page.tsx.
 */
export async function getWatchComparison(
  id: string,
  token: string,
  signal?: AbortSignal,
): Promise<import("@/lib/api/types").SearchResult> {
  const res = await fetch(`${apiBaseUrl()}/api/watches/${id}/comparison`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
    signal,
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new WatchesApiError(
      res.status,
      detail,
      `GET /api/watches/${id}/comparison returned ${res.status}`,
    );
  }
  const data = (await res.json()) as { result: unknown };
  return data.result as import("@/lib/api/types").SearchResult;
}

/**
 * Resolve a watch to its saved pSEO /c/<slug> comparison page (by matching the
 * watch's tracked retailers against each saved page's targets). Returns the
 * slug, or null when no saved page matches (caller falls back to the live
 * page). Cheap: an indexed read + in-memory overlap, no paid fetch.
 */
export async function getWatchComparisonSlug(
  id: string,
  token: string,
): Promise<string | null> {
  const res = await fetch(`${apiBaseUrl()}/api/watches/${id}/comparison-slug`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  });
  if (!res.ok) {
    throw new WatchesApiError(
      res.status,
      null,
      `GET /api/watches/${id}/comparison-slug returned ${res.status}`,
    );
  }
  const data = (await res.json()) as { slug: string | null };
  return data.slug;
}

export async function deleteWatch(id: string, token: string): Promise<void> {
  const res = await fetch(`${apiBaseUrl()}/api/watches/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text();
    }
    throw new WatchesApiError(
      res.status,
      detail,
      `DELETE /api/watches/${id} returned ${res.status}`,
    );
  }
}

export async function getWebPushPublicKey(): Promise<WebPushPublicKeyResponse> {
  const res = await fetch(`${apiBaseUrl()}/api/web-push/public-key`, {
    cache: "no-store",
  });
  if (!res.ok) {
    throw new WatchesApiError(res.status, null, "Could not load public key");
  }
  return (await res.json()) as WebPushPublicKeyResponse;
}

export async function subscribeWebPush(
  body: { endpoint: string; p256dh_key: string; auth_key: string },
  token: string,
): Promise<void> {
  await _post("/api/web-push/subscribe", body, token);
}

/** Convert a base64url VAPID public key to the Uint8Array format
 * the browser's PushManager.subscribe expects. */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
